import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import R from 'ramda';
import uuid from '@cotyorg/ccx-utils/js/helpers/uuid';
import { HeroFullBlock } from '@cotyorg/ccx-organism-hero';
import { CardoCarousel } from '@cotyorg/ccx-organism-carousel';
import Heading from '@cotyorg/ccx-atoms/Heading';
import Paragraph from '@cotyorg/ccx-atoms/Paragraph';
import { trackCarouselClickEvent } from '@cotyorg/ccx-utils/js/tracking/adobe-dtm/events/carousel';
import OptionalSection from '@cotyorg/ccx-utils/js/components/OptionalSection';
import { VideoPlayer } from '@cotyorg/ccx-organism-video';
import { trackVideoPlayEvent } from '@cotyorg/ccx-utils/js/tracking/adobe-dtm/events/video';
import Picture from '@cotyorg/ccx-atoms/Picture';
import StandardLayout from '../../templates/StandardLayout';
import ProductGallery from '../../organisms/ProductGallery';
import ProductInfo from '../../organisms/ProductInfo';
import FragranceNotes from '../../molecules/FragranceNotes';

const trackVideoEvent = ({ url, duration, progress }) =>
  trackVideoPlayEvent({
    url,
    duration,
    progress,
  });

const ProductComponent = ({
  sectionHeadings,
  currentBreakpoint,
  productGallery,
  productDetails,
  fragranceNotes,
  videos,
}) => (
  <StandardLayout>
    <div className="product-page">
      <div className="content-wrapper">
        <div className="grid-container full">
          <div className="grid-x">
            <div className="cell xsmall-16">
              <HeroFullBlock key={uuid()} heroStoreDataKey="heroSection1" />
            </div>
          </div>
        </div>

        <OptionalSection
          renderCondition={
            !!R.path(
              ['additional_section_1', 'additional_image_1_description'],
              productDetails
            ) &&
            !!R.path(
              ['additional_section_1', 'additional_image_1', 'url'],
              productDetails
            )
          }
          section={() => (
            <div className="product-container grid-container">
              <div className="grid-x row-reverse">
                <div className="product-info__container cell xsmall-16 large-8 align-middle">
                  <div className="grid-container">
                    <div className="grid-x">
                      <div className="cell xsmall-16 large-10 large-offset-3 flex-container align-middle">
                        <div className="product-info flex-container flex-dir-column">
                          <div className="product-info__inner">
                            <Paragraph
                              extraClass="product-info__description"
                              testAttribute="additional_image_1_description"
                            >
                              {R.path(
                                [
                                  'additional_section_1',
                                  'additional_image_1_description',
                                ],
                                productDetails
                              )}
                            </Paragraph>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="cell xsmall-16 large-8">
                  <Picture
                    alt={R.path(
                      ['additional_section_1', 'additional_image_1_alt'],
                      productDetails
                    )}
                    src={R.path(
                      ['additional_section_1', 'additional_image_1', 'url'],
                      productDetails
                    )}
                  />
                </div>
              </div>
            </div>
          )}
        />
        <OptionalSection
          renderCondition={
            !!R.path(
              ['additional_section_2', 'additional_image_2_description'],
              productDetails
            ) &&
            !!R.path(
              ['additional_section_2', 'additional_image_2', 'url'],
              productDetails
            )
          }
          section={() => (
            <div className="product-container grid-container">
              <div className="grid-x">
                <div className="product-info__container cell xsmall-16 large-8 align-middle">
                  <div className="grid-container">
                    <div className="grid-x">
                      <div className="cell xsmall-16 large-10 large-offset-3 flex-container align-middle">
                        <div className="product-info flex-container flex-dir-column">
                          <div className="product-info__inner">
                            <Paragraph
                              extraClass="product-info__description"
                              testAttribute="additional_image_2_description"
                            >
                              {R.path(
                                [
                                  'additional_section_2',
                                  'additional_image_2_description',
                                ],
                                productDetails
                              )}
                            </Paragraph>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="cell xsmall-16 large-8">
                  <Picture
                    alt={R.path(
                      ['additional_section_2', 'additional_image_2_alt'],
                      productDetails
                    )}
                    src={R.path(
                      ['additional_section_2', 'additional_image_2', 'url'],
                      productDetails
                    )}
                  />
                </div>
              </div>
            </div>
          )}
        />

        <div className="product-container grid-container">
          <div className="grid-x">
            <div className="product-info__container cell xsmall-16 large-8 align-middle">
              <div className="grid-container">
                <div className="grid-x">
                  <div className="cell xsmall-16 large-10 large-offset-3 flex-container align-middle">
                    <ProductInfo
                      sectionHeadings={sectionHeadings}
                      productDetails={productDetails}
                      currentBreakpoint={currentBreakpoint}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="cell xsmall-16 large-8">
              <ProductGallery productGallery={productGallery} />
            </div>
          </div>
        </div>

        <FragranceNotes
          sectionHeadings={sectionHeadings}
          fragranceNotes={fragranceNotes}
        />

        <OptionalSection
          renderCondition={
            !!R.pathOr([], ['benefits', 0, 'benefit', 'image'], productDetails)
              .length
          }
          section={() => (
            <div className="product-container grid-container">
              <div className="grid-x center">
                {R.map(
                  (image) => (
                    <div className="cell xsmall-6 large-5">
                      <Picture alt={image.alt_text} src={image.image_lg} />
                    </div>
                  ),
                  R.pathOr(
                    [],
                    ['benefits', 0, 'benefit', 'image'],
                    productDetails
                  )
                )}
              </div>
            </div>
          )}
        />

        <OptionalSection
          renderCondition={!!R.prop('title', videos)}
          section={() => (
            <div className="page-video">
              <h2 className="heading hero__heading hero-full-block__heading heading--underline text--center">
                {R.prop('title', videos)}
              </h2>
              {R.prop('description', videos) && (
                <p className="page-video__description">
                  {R.prop('description', videos)}
                </p>
              )}
              <VideoPlayer
                playsinline
                autoPlay
                loop
                videosStoreDataKey="mainVideo"
                playIconSrc="/ccx-files/assets/play.svg"
                pauseIconSrc="/ccx-files/assets/pause.svg"
                muteIconSrc="/ccx-files/assets/mute.svg"
                volumeIconSrc="/ccx-files/assets/volume.svg"
                shrinkVideoIconSrc="/ccx-files/assets/shrink.svg"
                expandVideoIconSrc="/ccx-files/assets/expand.svg"
                muted
                onPlay={trackVideoEvent}
                showOnlyMuteControls
              />
            </div>
          )}
        />

        <div className="more-fragrances">
          <div className="grid-container">
            <Heading level={4} extraClass="more-fragrances__heading">
              {R.path(['moreFragrances', 'heading'], sectionHeadings)}
            </Heading>

            <CardoCarousel
              doNotLazyLoad
              cardoProps={{
                cardoStoreDataKey: 'moreFragrances',
                isTitleUnderlined: false,
                hasBackground: false,
                isSmallTitle: true,
                fullWidth: true,
                headingLevel: 5,
                showStarRating: false,
                fixedHeight: true,
                zoomOnHover: true,
                hideDescription: true,
              }}
              carouselProps={{
                carouselsStoreDataKey: 'moreFragrancesCarousel',
                headingLevel: 5,
                rightChevronIconSrc: '/ccx-files/assets/arrow-right.svg',
                leftChevronIconSrc: '/ccx-files/assets/arrow-left.svg',
                onIndexChange: ({ type, index, isForward }) =>
                  trackCarouselClickEvent({
                    page: 'home',
                    carousel: 'moreFragrancesCarousel',
                    type,
                    index,
                    isForward,
                  }),
              }}
              cardsPerPageMap={{
                xsmall: 1,
                small: 2,
                medium: 3,
                large: 4,
                xlarge: 4,
              }}
            />
          </div>
        </div>
      </div>
    </div>
  </StandardLayout>
);
ProductComponent.propTypes = {
  productDetails: PropTypes.shape({
    longDescription: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  }),
  sectionHeadings: PropTypes.objectOf(PropTypes.object),
  currentBreakpoint: PropTypes.string,
  videos: PropTypes.objectOf(PropTypes.object),
  productGallery: PropTypes.arrayOf(
    PropTypes.shape({
      src: PropTypes.string,
      srcset: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          src: PropTypes.string,
        })
      ),
    })
  ),
  fragranceNotes: PropTypes.shape({
    image: PropTypes.shape({
      imageSrc: PropTypes.string,
      imageSrcSet: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          src: PropTypes.string,
        })
      ),
      alt: PropTypes.string,
    }),
    notes: PropTypes.arrayOf(
      PropTypes.shape({
        heading: PropTypes.string,
        description: PropTypes.string,
      })
    ),
  }),
};

const mapStateToProps = ({ content, breakpoints }) => ({
  productDetails: R.prop('productDetails', content),
  hero: R.prop('hero', content),
  currentBreakpoint: R.prop('current', breakpoints),
  sectionHeadings: R.prop('sectionHeadings', content),
  productGallery: R.prop('productGallery', content),
  fragranceNotes: R.prop('fragranceNotes', content),
  videos: R.prop('videos', content),
});

export default connect(mapStateToProps)(ProductComponent);
